import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { http } from "../lib/api";

const AuthContext = createContext(null);

export const apiError = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return d.msg || String(d);
};

let refreshing = null;
http.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err.config;
    if (err.response?.status === 401 && !cfg._retry && !cfg.url.includes("/auth/")) {
      cfg._retry = true;
      refreshing = refreshing || http.post("/auth/refresh").finally(() => (refreshing = null));
      try {
        await refreshing;
        return http(cfg);
      } catch {
        return Promise.reject(err);
      }
    }
    return Promise.reject(err);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);

  const check = useCallback(async () => {
    try {
      setUser((await http.get("/auth/me")).data);
    } catch {
      try {
        await http.post("/auth/refresh");
        setUser((await http.get("/auth/me")).data);
      } catch {
        setUser(false);
      }
    }
  }, []);

  useEffect(() => {
    // AuthCallback exchanges session_id first when returning from Google
    if (window.location.hash?.includes("session_id=")) return;
    check();
  }, [check]);

  const login = async (email, password) => setUser((await http.post("/auth/login", { email, password })).data);
  const register = async (name, email, password) => setUser((await http.post("/auth/register", { name, email, password })).data);
  const logout = async () => {
    await http.post("/auth/logout").catch(() => {});
    setUser(false);
  };
  const googleLogin = (next = "/account") => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + next;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, googleLogin, refresh: check }}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
